import React, { useRef, useState, useEffect } from "react";
import { Tooltip, Box } from "@mui/material";

interface OverflowTextProps {
  children: JSX.Element | string;
  maxWidth?: number;
  minWidth?: number;
  className?: string;
}

const OverflowText: React.FC<OverflowTextProps> = ({
  children,
  maxWidth = 150,
  minWidth = 50,
  className,
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        const { scrollWidth, clientWidth } = textRef.current;
        setIsOverflowing(scrollWidth > clientWidth);
      }
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    if (textRef.current) {
      resizeObserver.observe(textRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [children]);

  return (
    <Tooltip
      title={isOverflowing ? children : ""}
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "rgb(14 14 14 / 92%)",
            "& .MuiTooltip-arrow": {
              color: "rgb(14 14 14 / 92%)",
            },
            fontSize: "13px",
          },
        },
      }}
    >
      <Box
        ref={textRef}
        sx={{
          maxWidth: `${maxWidth}px`,
          minWidth: `${minWidth}px`,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          cursor: isOverflowing ? "pointer" : "default",
        }}
      >
        <span className={`text-wrapper ${className}`}>{children}</span>
      </Box>
    </Tooltip>
  );
};

export default OverflowText;
